import { Flex, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { ForecastedCycle } from '@aslanpay/portal-common-lib';

import CenterSpinner from 'app/components/UI/CenterSpinner';
import { formatDateTime } from 'app/utils/table/campaigns';

interface ScheduleForecastProps {
  cycles: ForecastedCycle[];
  isLoading: boolean;
  isError: boolean;
  /** The dates cannot be worked out until the schedule above is complete, which is worth saying. */
  needsSchedule: boolean;
}

/**
 * The dates the repeat actually produces.
 * <p>
 * A rule read back as words still leaves a reader working out which days those are, months ahead. The service
 * works them out the way it will when the campaign runs, so this is the answer rather than an approximation of
 * it.
 */
function ScheduleForecast({ cycles, isLoading, isError, needsSchedule }: ScheduleForecastProps) {
  if (needsSchedule) {
    return (
      <Text
        textStyle="extraSmallRegular"
        color="brand.darkGray"
        data-testid="forecast-needs-schedule"
      >
        Set the schedule above and the dates this produces will be listed here.
      </Text>
    );
  }

  if (isLoading) return <CenterSpinner />;

  if (isError) {
    return (
      <Text textStyle="extraSmallRegular" color="brand.darkGray" data-testid="forecast-unavailable">
        The dates this produces could not be worked out. The campaign can still be saved.
      </Text>
    );
  }

  if (cycles.length === 0) return null;

  return (
    <Flex flexDir="column" gap="6px" data-testid="schedule-forecast">
      <Text textStyle="extraSmallRegular" color="brand.darkGray">
        The next {cycles.length} {cycles.length === 1 ? 'run' : 'runs'} this produces. The first is
        the schedule above.
      </Text>
      <Flex overflowX="auto" maxH="260px" overflowY="auto">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Run</Th>
              <Th>Nominations open</Th>
              <Th>Nominations close</Th>
              <Th>Review opens</Th>
              <Th>Review closes</Th>
              <Th>Winner announced</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cycles.map((cycle) => (
              <Tr key={cycle.sequence} data-testid={`forecast-cycle-${cycle.sequence}`}>
                <Td>{cycle.sequence}</Td>
                <Td>{formatDateTime(cycle.nominationsOpenAt)}</Td>
                <Td>{formatDateTime(cycle.nominationsCloseAt)}</Td>
                <Td>{formatDateTime(cycle.reviewOpensAt)}</Td>
                <Td>{formatDateTime(cycle.reviewClosesAt)}</Td>
                <Td>{formatDateTime(cycle.announcementAt)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Flex>
    </Flex>
  );
}

export default ScheduleForecast;
